/**
 * The /lp landing variants: twenty standalone pages for conversion testing.
 * Each is noindex and canonicalizes to `/`. Traffic is split by linking ads,
 * emails or posts to a specific /lp/<slug>; page_viewed / cta_clicked /
 * signup events carry the path, and CTA events also carry `lp_variant`.
 */
import type { Component } from 'vue'
import type { LpGoal } from '@/components/lp/useLp'

export interface LpEntry {
    slug: string
    name: string
    goal: LpGoal
    /** One line: what the page is. */
    concept: string
    /** What this variant is testing. */
    hypothesis: string
    /** Browser title + meta description. */
    title: string
    description: string
    /** Two or three swatches for the index page. */
    swatch: string[]
    /** The flagship set: built to be the strongest challengers to the live page. */
    flagship?: boolean
    component: () => Promise<Component | { default: Component }>
}

export const LP_ENTRIES: LpEntry[] = [
    {
        slug: 'inside',
        name: 'Inside Lighthouse',
        goal: 'trial',
        flagship: true,
        concept: 'A scroll-scrubbed product film: one Glossier deal runs from first email to "paid" inside a full-size replica of the app, with a camera that pans and zooms, day-and-time captions and a chapter HUD.',
        hypothesis: 'Watching the whole lifecycle happen in the real UI (and controlling the playhead) makes the value undeniable and lifts trial starts.',
        title: 'Watch a brand deal run itself',
        description: 'Scroll through one real week in Lighthouse: the reply, the project, the contract, the calendar, the browser task, the invoice. 30 days free.',
        swatch: ['#f1efea', '#ffffff', '#fb4c1f'],
        component: () => import('./LpInside.vue'),
    },
    {
        slug: 'inbox-zero',
        name: 'Inbox Avalanche',
        goal: 'trial',
        concept: 'An avalanche of brand emails buries the hero, then scrolling sorts them into Top Opportunities, Worth a Look and Likely Spam, each with a reply drafted.',
        hypothesis: 'Visceral overwhelm then relief (the current page’s flying emails, pushed to a payoff) lifts trial starts.',
        title: 'Inbox zero for creators',
        description: 'Lighthouse reads every brand email, drafts the reply in your voice, and sorts the rest. You just hit send. Start free for 30 days.',
        swatch: ['#ffffff', '#141414', '#fb4c1f'],
        component: () => import('./LpInboxZero.vue'),
    },
    {
        slug: 'night-shift',
        name: 'Night Shift',
        goal: 'trial',
        concept: 'A lighthouse beam sweeps a night sky; scrolling runs the clock from 11pm to 7am as the page itself turns from night to sunrise.',
        hypothesis: 'Standing goals ("it works while you sleep") is the most emotionally resonant benefit of the new agent.',
        title: 'It works while you sleep',
        description: 'Give Lighthouse a standing goal. It triages your inbox overnight, drafts every reply, and has it all waiting for you in the morning.',
        swatch: ['#0b1022', '#f7b267', '#fb4c1f'],
        component: () => import('./LpNightShift.vue'),
    },
    {
        slug: 'ask',
        name: 'Just Ask',
        goal: 'trial',
        concept: 'A dark command-palette page. The hero is a live ⌘K box: pick or type a request and watch the plan run, step by step, into finished drafts.',
        hypothesis: 'Tech-forward creators convert on interactivity; letting them "use" the agent on the page beats watching it.',
        title: 'Just ask. It does the work.',
        description: '205 things your creator assistant can do, one sentence away. Follow up on every pitch, review a contract, chase an invoice.',
        swatch: ['#0c0c0d', '#ff6a3d', '#f2efe9'],
        component: () => import('./LpAsk.vue'),
    },
    {
        slug: 'keep-100',
        name: 'Keep 100%',
        goal: 'trial',
        concept: 'A finance-grade commission calculator: drag your yearly deal income and see a manager’s 20% next to $99/mo, on rolling odometer digits.',
        hypothesis: 'A concrete dollar figure ("you keep $23,060 more") is the strongest trial driver for mid-size creators.',
        title: 'Keep 100% of every brand deal',
        description: 'Managers take 20%. Lighthouse takes 0%. See exactly how much you keep, then start free for 30 days.',
        swatch: ['#f2ede3', '#16130f', '#fb4c1f'],
        component: () => import('./LpKeep100.vue'),
    },
    {
        slug: 'manager',
        name: 'The Manager Question',
        goal: 'demo',
        concept: 'An editorial magazine feature with a draggable before/after inbox, columns, pull quotes, and "office hours" booking at the end.',
        hypothesis: 'Bigger creators weighing a manager need a considered read and a human conversation, not a trial button.',
        title: 'Do you need a manager?',
        description: 'Before you sign away 20%, see what an assistant can do. Book a 20-minute walkthrough with the Lighthouse team.',
        swatch: ['#f3efe6', '#111111', '#e5481f'],
        component: () => import('./LpManager.vue'),
    },
    {
        slug: 'one-deal',
        name: 'The Life of a Deal',
        goal: 'trial',
        concept: 'One Glossier deal followed from first email to paid invoice on a pinned horizontal track, with a value meter filling to $4,800.',
        hypothesis: 'Showing the whole lifecycle in one story makes the "all-in-one" value obvious without a feature list.',
        title: 'From first email to paid invoice',
        description: 'Follow one brand deal from inbox to payout and watch Lighthouse handle every step. Start free for 30 days.',
        swatch: ['#f6f0e6', '#fb4c1f', '#2a2522'],
        component: () => import('./LpOneDeal.vue'),
    },
    {
        slug: 'demo',
        name: 'Concierge Demo',
        goal: 'demo',
        concept: 'A dark, premium page whose hero is the booking calendar itself, with a timed agenda and a cursor-lit background.',
        hypothesis: 'Removing every step between intent and a booked call (calendar above the fold) maximizes demo bookings.',
        title: 'Book a Lighthouse demo',
        description: 'A 20-minute live walkthrough of Lighthouse with our team. See your inbox, deals and invoices handled, and the new agent at work.',
        swatch: ['#13100e', '#e9d9c2', '#fb4c1f'],
        component: () => import('./LpDemo.vue'),
    },
    {
        slug: 'text',
        name: 'Text Your Assistant',
        goal: 'trial',
        concept: 'The whole page is a text thread with your assistant in liquid glass: scrolling plays the conversation, cards and all.',
        hypothesis: 'Framing Lighthouse as a person you text (mobile-native) converts better with phone-first creators.',
        title: 'Your business, one text away',
        description: 'Text your assistant like a friend. It drafts replies, counters offers, flags contracts and chases invoices. Start free.',
        swatch: ['#fde8dc', '#c9d4ff', '#fb4c1f'],
        component: () => import('./LpText.vue'),
    },
    {
        slug: 'name',
        name: 'Name Your Assistant',
        goal: 'trial',
        concept: 'A "Hello my name is" sticker asks what you’d call your assistant; the name flows through the whole page, down to its résumé.',
        hypothesis: 'Personalization creates ownership (endowment effect) before signup.',
        title: 'Name your new assistant',
        description: 'Every creator deserves an assistant. Name yours, then hire it free for 30 days. $99/mo after, 0% commission.',
        swatch: ['#fff3cf', '#1d1b16', '#fb4c1f'],
        component: () => import('./LpName.vue'),
    },
    {
        slug: 'contracts',
        name: 'Contract X-Ray',
        goal: 'trial',
        concept: 'A scanning beam reads a brand agreement and pins red flags in the margins: perpetual usage, wide exclusivity, Net 90.',
        hypothesis: 'Fear of a bad contract is a sharper pain than inbox load for creators who have been burned.',
        title: 'Contract review for creators',
        description: 'Lighthouse reads every brand contract and flags usage rights, exclusivity and payment terms in plain English. Start free.',
        swatch: ['#0e1116', '#e8eef7', '#ff5a2a'],
        component: () => import('./LpContracts.vue'),
    },
    {
        slug: 'get-paid',
        name: 'Get Paid',
        goal: 'trial',
        concept: 'A thermal-receipt world: invoices print, OVERDUE stamps slam, a polite nudge is drafted, then PAID. Pricing is a tear-off receipt.',
        hypothesis: 'Money owed is the most urgent pain; an invoices-first page converts creators with overdue payments.',
        title: 'Get paid on time',
        description: 'Invoices in one sentence, overdue payments chased politely, every dollar tracked. Start free for 30 days.',
        swatch: ['#f7f4ec', '#1b1b1b', '#22a05a'],
        component: () => import('./LpGetPaid.vue'),
    },
    {
        slug: 'pitch',
        name: 'Pitch Machine',
        goal: 'trial',
        concept: 'A playful, sticker-bright page with a swipeable deck of brand matches, pitches that write themselves, and replies pinging in.',
        hypothesis: 'Growth-minded creators want more deals, not less admin; leading with outbound pitching wins them.',
        title: 'Pitch brands on autopilot',
        description: 'Lighthouse finds brands that fit you, writes the pitch in your voice, and follows up until they reply. Start free.',
        swatch: ['#fff8ef', '#ff7ab6', '#c4f25a'],
        component: () => import('./LpPitch.vue'),
    },
    {
        slug: 'os',
        name: 'Creator OS',
        goal: 'hybrid',
        concept: 'Apple-clean product page: a 3D-tilted live dashboard that flattens as you scroll, then a bento grid of animated mini demos.',
        hypothesis: 'A polished, complete "operating system" impression earns trust from creators comparing tools.',
        title: 'The operating system for creator businesses',
        description: 'Inbox, deals, contracts, calendar, pitching, media kit and payments in one place, with an assistant that runs it. Try free or book a demo.',
        swatch: ['#fbfbfa', '#111111', '#fb4c1f'],
        component: () => import('./LpOs.vue'),
    },
    {
        slug: 'calm',
        name: 'Chaos to Calm',
        goal: 'trial',
        concept: 'A two-state page with one giant switch: "Without" is sticky-note chaos, "With" snaps everything into calm, aligned order.',
        hypothesis: 'Letting visitors flip their own before/after is more persuasive than showing it.',
        title: 'From chaos to calm',
        description: 'Running a creator business is chaos. Lighthouse makes it calm: replies drafted, contracts checked, invoices chased. Start free.',
        swatch: ['#fff6d6', '#f4f1ea', '#fb4c1f'],
        component: () => import('./LpCalm.vue'),
    },
    {
        slug: 'film',
        name: 'Now Showing',
        goal: 'hybrid',
        concept: 'A cinema night: chasing marquee bulbs, curtains, the launch film on the big screen, a feature-cast list, critic quotes and rolling credits.',
        hypothesis: 'Leaning into the brand films (inspired by the current film archive) builds affinity and lifts both trials and demos.',
        title: 'Now showing: Lighthouse',
        description: 'Watch the Lighthouse films, then start your free trial or book a private screening with the team.',
        swatch: ['#120a0a', '#7a1414', '#ffcf6b'],
        component: () => import('./LpFilm.vue'),
    },
    {
        slug: 'letter',
        name: "Founders' Letter",
        goal: 'demo',
        concept: 'An envelope opens into a handwritten-feeling letter from Kevin and Ryan, margin notes and a polaroid, ending in a booking postcard.',
        hypothesis: 'A personal, founder-led invitation earns more demo bookings than product marketing.',
        title: 'A letter from the founders',
        description: 'Why we built Lighthouse, and an invitation to see it for yourself. Book 20 minutes with the team.',
        swatch: ['#efe6d6', '#2b2118', '#c2410c'],
        component: () => import('./LpLetter.vue'),
    },
    {
        slug: 'quiz',
        name: 'Time Leak Quiz',
        goal: 'hybrid',
        concept: 'Four quick questions find your "time leak", then a personalized result routes heavy hitters to a demo and everyone else to a trial.',
        hypothesis: 'Qualifying visitors and routing them to the right CTA beats one CTA for everyone.',
        title: 'How much is admin costing you?',
        description: 'Four questions, thirty seconds. Find out how many hours and dollars your creator business is leaking, and how to get them back.',
        swatch: ['#ff5a2a', '#ff2e7e', '#6b3dff'],
        component: () => import('./LpQuiz.vue'),
    },
    {
        slug: 'loud',
        name: 'Loud',
        goal: 'trial',
        concept: 'Neo-brutalist poster energy: giant type, crossing marquees that skew with scroll speed, stickers, hard shadows.',
        hypothesis: 'Gen-Z creators respond to bold, irreverent design over polished SaaS.',
        title: 'Less inbox. More content.',
        description: 'Lighthouse replies to brands, reads contracts, chases money and pitches for you. 30 days free. 0% commission.',
        swatch: ['#ff4a1c', '#111111', '#f6efe3'],
        component: () => import('./LpLoud.vue'),
    },
    {
        slug: 'spreadsheet',
        name: 'Retire the Spreadsheet',
        goal: 'trial',
        concept: 'Starts as a messy brand_deals_FINAL_v7 sheet; scrolling lifts the rows into a live project board and pulls the old tool stack into one logo.',
        hypothesis: 'Creators who track deals in spreadsheets and Notion instantly see the upgrade.',
        title: 'Retire the brand deal spreadsheet',
        description: 'Lighthouse builds your deal board from your inbox automatically, no spreadsheet required. Start free for 30 days.',
        swatch: ['#ffffff', '#1e7a46', '#fb4c1f'],
        component: () => import('./LpSpreadsheet.vue'),
    },
]

// Pure: the native build drops the /lp pages (router/index.ts) and this table with them.
export const LP_BY_SLUG = /* @__PURE__ */ (() => Object.fromEntries(LP_ENTRIES.map((e) => [e.slug, e])) as Record<string, LpEntry>)()
